import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Users,
  Shapes,
  Dumbbell,
  Plus,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api.service';
import { Event } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

interface DashboardStats {
  totalEvents: number;
  activeEvents: number;
  totalTeams: number;
  totalCategories: number;
  totalWODs: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalEvents: 0,
    activeEvents: 0,
    totalTeams: 0,
    totalCategories: 0,
    totalWODs: 0
  });
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [eventsRes, teamsRes, categoriesRes, wodsRes] = await Promise.all([
        apiService.getEvents(),
        apiService.getTeams(),
        apiService.getCategories(),
        apiService.getWODs()
      ]);

      const events = eventsRes.data || [];
      const teams = teamsRes.data.teams || [];
      const categories = categoriesRes.data.categories || [];
      const wods = wodsRes.data.wods || [];

      setStats({
        totalEvents: events.length,
        activeEvents: events.filter((e: Event) => e.status === 'active').length,
        totalTeams: teams.length,
        totalCategories: categories.length,
        totalWODs: wods.length
      });

      setRecentEvents(events.slice(0, 5));
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'published': return 'bg-info/15 text-info';
      case 'active': return 'bg-success/20 text-success';
      case 'completed': return 'bg-white/[0.10] text-foreground';
      case 'archived': return 'bg-white/[0.06] text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const StatCard = ({ title, value, icon, onClick }: { title: string; value: number; icon: React.ReactNode; onClick?: () => void }) => (
    <Card
      className={cn(
        "transition-[transform,border-color] duration-150",
        onClick && "cursor-pointer hover:border-primary/50 active:scale-[0.99]"
      )}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-3xl font-bold">{value}</span>
            <p className="text-sm text-muted-foreground mt-1">{title}</p>
          </div>
          <div className="text-foreground opacity-70">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          title={t('dashboard.totalEvents')}
          value={stats.totalEvents}
          icon={<Calendar size={32} />}
          onClick={() => navigate('/admin/events')}
        />
        <StatCard
          title={t('dashboard.activeEvents')}
          value={stats.activeEvents}
          icon={<Calendar size={32} />}
          onClick={() => navigate('/admin/events?status=active')}
        />
        <StatCard
          title={t('dashboard.totalTeams')}
          value={stats.totalTeams}
          icon={<Users size={32} />}
          onClick={() => navigate('/admin/teams')}
        />
        <StatCard
          title={t('dashboard.totalCategories')}
          value={stats.totalCategories}
          icon={<Shapes size={32} />}
          onClick={() => navigate('/admin/categories')}
        />
        <StatCard
          title={t('dashboard.totalWODs')}
          value={stats.totalWODs}
          icon={<Dumbbell size={32} />}
          onClick={() => navigate('/admin/wods')}
        />
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Événements récents */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-foreground">
                {t('dashboard.recentEvents')}
              </h2>
              <button
                className="p-2 rounded-md hover:bg-muted transition-colors text-foreground"
                onClick={() => navigate('/admin/events/new')}
              >
                <Plus size={20} />
              </button>
            </div>

            <div className="space-y-2">
              {recentEvents.map((event) => (
                <div
                  key={event.id}
                  className="p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/events/${event.id}/settings`)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{event.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {event.date_start ? new Date(event.date_start).toLocaleDateString() : t('dashboard.dateNotDefined')} - {event.location}
                      </p>
                    </div>
                    <span className={cn(
                      "px-2 py-1 rounded-md text-xs font-medium",
                      getStatusBadgeClass(event.status)
                    )}>
                      {event.status}
                    </span>
                  </div>
                </div>
              ))}

              {recentEvents.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  {t('dashboard.noEvents')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions rapides */}
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-lg font-medium text-foreground mb-4">
              {t('dashboard.quickActions')}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => navigate('/admin/events/new')}
                className="w-full"
              >
                {t('dashboard.newEvent')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/categories?create=true')}
                className="w-full"
              >
                {t('dashboard.newCategory')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/wods?create=true')}
                className="w-full"
              >
                {t('dashboard.newWOD')}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/archives')}
                className="w-full"
              >
                {t('nav.archives')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
